import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Thermometer, Eye, CheckSquare, Clock, MapPin, Utensils, Wine, Building2 } from "lucide-react";

interface TemplateItem {
  id: string;
  name: string;
  type: string;
  description?: string;
  category?: string;
  min_value?: number;
  max_value?: number;
  unit?: string;
  required?: boolean;
}

interface ItemCardProps {
  item: TemplateItem;
  isSelected: boolean;
  onToggle: () => void;
}

export const ItemCard = ({ item, isSelected, onToggle }: ItemCardProps) => {
  const getItemIcon = (type: string, category?: string) => {
    switch (type) {
      case 'temperature':
        return <Thermometer className="h-4 w-4" />;
      case 'visual':
        return <Eye className="h-4 w-4" />;
      case 'checkbox':
        return <CheckSquare className="h-4 w-4" />;
      case 'time':
        return <Clock className="h-4 w-4" />;
      default:
        // Use category icons as fallback
        switch (category) {
          case 'kitchen':
            return <Utensils className="h-4 w-4" />;
          case 'bar':
            return <Wine className="h-4 w-4" />;
          case 'restaurant':
            return <Building2 className="h-4 w-4" />;
          case 'external':
            return <MapPin className="h-4 w-4" />;
          default:
            return <CheckSquare className="h-4 w-4" />;
        }
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'temperature':
        return 'Temperature';
      case 'visual':
        return 'Visual Check';
      case 'checkbox':
        return 'Yes/No';
      case 'time':
        return 'Time Record';
      default:
        return 'Check';
    }
  };

  const getCategoryColor = (category?: string) => {
    switch (category) {
      case 'kitchen':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'bar':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'restaurant':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'external':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatValueRange = () => {
    if (item.min_value !== undefined || item.max_value !== undefined) {
      const min = item.min_value !== undefined ? item.min_value : '-∞';
      const max = item.max_value !== undefined ? item.max_value : '+∞';
      const unit = item.unit ? ` ${item.unit}` : '';
      
      if (item.min_value !== undefined && item.max_value !== undefined) {
        return `${min} - ${max}${unit}`;
      } else if (item.min_value !== undefined) {
        return `≥ ${min}${unit}`;
      } else if (item.max_value !== undefined) {
        return `≤ ${max}${unit}`;
      }
    }
    return null;
  };

  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-md ${
        isSelected ? 'ring-2 ring-primary bg-primary/5' : ''
      } ${item.required ? 'border-orange-200 bg-orange-50/50' : ''}`}
      onClick={onToggle}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2 flex-1">
            <Checkbox 
              checked={isSelected}
              onChange={() => {}} // Controlled by parent click
              className="mt-0.5"
            />
            <div className="flex items-center gap-2">
              {getItemIcon(item.type, item.category)}
              <span className="font-medium text-sm leading-tight">{item.name}</span>
            </div>
          </div>
          {item.required && (
            <Badge variant="outline" className="text-xs bg-orange-100 text-orange-800 border-orange-300">
              Required
            </Badge>
          )}
        </div>

        {item.description && (
          <p className="text-xs text-muted-foreground leading-relaxed">
            {item.description}
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="text-xs">
            {getTypeLabel(item.type)}
          </Badge>
          
          {item.category && (
            <Badge className={`text-xs ${getCategoryColor(item.category)}`}>
              {item.category}
            </Badge>
          )}

          {formatValueRange() && (
            <Badge variant="outline" className="text-xs">
              {formatValueRange()}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};